import * as React from 'react';
import { VtxIcon, VtxTag, VtxTooltip } from '@vertexinc/vtx-ui-react-component-library';
import BreadcrumbsProps from './BreadcrumbsProps.interface';
import {
  getFilterDescription,
  getSorterDescription,
  GridFilter,
  GridSort,
  ShowBreadCrumbsEnum,
  TableColumn,
  TableSettings,
} from '../..';
import './breadcrumbs.scss';
import { ShowTableColumnsEnum } from '../GenericGrid/GenericGridProps.interface';

const maxDescriptionLength = 100;

const Breadcrumbs = (
  {
    filters,
    sorters,
    tableSettings,
    defaultTableSettings,
    alwaysHiddenFilterBreadcrumbs,
    alwaysHiddenSorterBreadcrumbs,
    onDeleteFilter,
    onDeleteSorter,
    showBreadcrumbs,
    showTableColumns,
  }: BreadcrumbsProps,
): JSX.Element => {
  const onCloseFilter = (event: React.MouseEvent<HTMLElement, MouseEvent>, gridFilter: GridFilter) => {
    event.preventDefault();
    onDeleteFilter(gridFilter);
  };

  const onCloseSorter = (event: React.MouseEvent<HTMLElement, MouseEvent>, gridSort: GridSort) => {
    event.preventDefault();
    onDeleteSorter(gridSort);
  };

  // Render the Sorter Breadcrumbs
  const renderSorterBreadcrumbs = (): JSX.Element[] => {
    const breadcrumbs: JSX.Element[] = [];

    if (sorters) {
      // Get the Table Settings, otherwise use the Default Table Settings
      const settings: TableSettings = tableSettings || defaultTableSettings;

      // Show the Breadcrumbs in the order the Columns is displayed
      settings.columns.forEach((setting: TableColumn) => {
        const sorter: GridSort = sorters.filter((x) => x.column === setting.dataIndex)[0];

        // Sorter configured for the Column
        if (sorter) {
          if (!alwaysHiddenSorterBreadcrumbs?.includes(sorter.column)) {
            const defaultColumn: TableColumn = defaultTableSettings?.columns?.filter((column: TableColumn) => column.dataIndex === sorter.column)[0];
            const tableColumn: TableColumn = tableSettings?.columns?.filter((column: TableColumn) => column.dataIndex === sorter.column)[0];
            const visibility = tableColumn
              ? showTableColumns === ShowTableColumnsEnum.ALL ? true : tableColumn.visibility
              : defaultColumn.visibility;

            // Determine if the Breadcrumb must be shown
            const showBreadcrumb = (showBreadcrumbs === ShowBreadCrumbsEnum.ALL)
              || ((showBreadcrumbs === ShowBreadCrumbsEnum.VISIBLE) && visibility)
              || ((showBreadcrumbs === ShowBreadCrumbsEnum.HIDDEN) && !visibility);

            if (showBreadcrumb) {
              breadcrumbs.push(
                <VtxTag
                  key={sorter.column}
                  closable
                  color={visibility === true ? 'purple' : 'default'}
                  onClose={(event: React.MouseEvent<HTMLElement, MouseEvent>) => onCloseSorter(event, sorter)}
                >
                  <div className="breadcrumb">
                    <div className="breadcrumbIcon">
                      <VtxIcon type={sorter.order === 'asc' ? 'sort-ascending' : 'sort-descending'} fontSize={14} />
                    </div>
                    <div className="breadcrumbColumn">
                      {defaultColumn?.name}
                    </div>
                    <div className="breadcrumbFilter">
                      {getSorterDescription(sorter)}
                    </div>
                  </div>
                </VtxTag>,
              );
            }
          }
        }
      });
    }

    return breadcrumbs;
  };

  // Render the Filter Breadcrumbs
  const renderFilterBreadcrumbs = (): JSX.Element[] => {
    const breadcrumbs: JSX.Element[] = [];

    if (filters) {
      // Get the Table Settings, otherwise use the Default Table Settings
      const settings: TableSettings = tableSettings || defaultTableSettings;

      // Show the Breadcrumbs in the order the Columns is displayed
      settings.columns.forEach((setting: TableColumn) => {
        const filter: GridFilter = filters.filter((x) => x.column === setting.dataIndex)[0];

        // Filter configured for the Column
        if (filter) {
          // Filter is not always Hidden
          if (!alwaysHiddenFilterBreadcrumbs?.includes(filter.column)) {
            const defaultColumn: TableColumn = defaultTableSettings?.columns?.filter((column: TableColumn) => column.dataIndex === filter.column)[0];
            const tableColumn: TableColumn = tableSettings?.columns?.filter((column: TableColumn) => column.dataIndex === filter.column)[0];
            const visibility = tableColumn
              ? showTableColumns === ShowTableColumnsEnum.ALL ? true : tableColumn.visibility
              : defaultColumn.visibility;

            // Determine if the Breadcrumb must be shown
            const showBreadcrumb = (showBreadcrumbs === ShowBreadCrumbsEnum.ALL)
              || ((showBreadcrumbs === ShowBreadCrumbsEnum.VISIBLE) && visibility)
              || ((showBreadcrumbs === ShowBreadCrumbsEnum.HIDDEN) && !visibility);

            if (showBreadcrumb) {
              const description = getFilterDescription(filter);
              if (description.length < maxDescriptionLength) {
                breadcrumbs.push(
                  <VtxTag
                    key={filter.column}
                    closable
                    color={visibility === true ? 'blue' : 'default'}
                    onClose={(event: React.MouseEvent<HTMLElement, MouseEvent>) => onCloseFilter(event, filter)}
                  >
                    <div className="breadcrumb">
                      <div className="breadcrumbIcon">
                        <VtxIcon type="filter" fontSize={14} />
                      </div>
                      <div className="breadcrumbColumn">
                        {defaultColumn?.name}
                      </div>
                      <div className="breadcrumbFilter">
                        {description}
                      </div>
                    </div>
                  </VtxTag>,
                );
              } else {
                const descriptionTruncated = `${description.substring(0, maxDescriptionLength)} ...`;

                breadcrumbs.push(
                  <VtxTooltip title={description}>
                    <VtxTag
                      key={filter.column}
                      closable
                      color={visibility === true ? 'blue' : 'default'}
                      onClose={(event: React.MouseEvent<HTMLElement, MouseEvent>) => onCloseFilter(event, filter)}
                    >
                      <div className="breadcrumb">
                        <div className="breadcrumbIcon">
                          <VtxIcon type="filter" fontSize={14} />
                        </div>
                        <div className="breadcrumbColumn">
                          {defaultColumn?.name}
                        </div>
                        <div className="breadcrumbFilter">
                          {descriptionTruncated}
                        </div>
                      </div>
                    </VtxTag>
                  </VtxTooltip>,
                );
              }
            }
          }
        }
      });
    }

    return breadcrumbs;
  };

  const sorterBreadcrumbs: JSX.Element[] = renderSorterBreadcrumbs();
  const filterBreadcrumbs: JSX.Element[] = renderFilterBreadcrumbs();

  // No Sorters of Filters
  if ((filterBreadcrumbs.length === 0) && (sorterBreadcrumbs.length === 0)) {
    return (<></>);
  }

  return (
    <div className="breadcrumbContainer">
      {sorterBreadcrumbs}
      {filterBreadcrumbs}
    </div>
  );
};

export default Breadcrumbs;
